import { parseAsString, useQueryState } from 'nuqs';
import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { useNotification } from '../../../../contexts/NotificationContext';
import { usePuppyMode } from '../../../../hooks';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import { useSearchUsers } from '../../hooks';
import { normalizeSearchKeywords } from '../../utils/phoneSearch';
import { UsersHeader } from '../UsersHeader';
import { UsersTable } from '../UsersTable';

interface UserListProps {}

// NOTE(#104): the login-method filter (client-side row filtering) was removed.
// The backend `userSearch` resolver does not implement
// `UserSearchFilter.loginMethod` (the CRM-187 schema field is a no-op), and
// filtering the fetched page locally caused silent false negatives and hid
// the pagination footer. See TableControls.tsx for the full context.
export const UserList: React.FC<UserListProps> = () => {
  // Keeps `search` in sync with both React state and the URL query string.
  const [searchTerm, setSearchTerm] = useQueryState(
    'search',
    parseAsString.withDefault('')
  );
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { addNotification } = useNotification();
  const { puppyFilter } = usePuppyMode();

  const captureException = useCallback(() => {
    addNotification({
      title: 'Error Loading Users',
      message: 'An error occurred while searching for users. Please try again.',
      type: 'error',
    });
  }, [addNotification]);

  const searchConfig = useMemo(
    () => ({
      initialPageSize: DEFAULT_PAGE_SIZE,
      captureException,
    }),
    [captureException]
  );

  const {
    users,
    totalUsers,
    isLoading,
    currentPage,
    hasNextPage,
    searchUsers,
    goToNextPage,
    goToPreviousPage,
    refetch,
  } = useSearchUsers(searchConfig);

  // Re-issue a fresh search (page 1) whenever keywords, puppy filter, or page
  // size changes. The hook resets the cursor stack internally.
  useEffect(() => {
    // Digits-only queries are normalized to their '+'-prefixed variant so
    // phone numbers pasted without the '+' still match (#108).
    const keywords = normalizeSearchKeywords(searchTerm);
    const queryParams = {
      ...(keywords && { keywords }),
      filter: {
        ...(puppyFilter !== undefined && { isPuppy: puppyFilter }),
      },
      limit: pageSize,
    };

    searchUsers(queryParams);
  }, [searchTerm, puppyFilter, pageSize, searchUsers]);

  useEffect(() => {
    document.title = 'User Management - Choco Admin';
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      if (newPage > currentPage) {
        goToNextPage();
      } else if (newPage < currentPage) {
        goToPreviousPage();
      }
    },
    [currentPage, goToNextPage, goToPreviousPage]
  );

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    // searchUsers will be called by the effect above when pageSize changes,
    // which also resets the cursor stack to page 1.
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    // The effect reacts to searchTerm changes and resets cursor stack.
  };

  // When hasNextPage is true but totalUsers doesn't yet reflect further pages,
  // bump totalPages so the Next button stays enabled.
  const totalPages = Math.max(
    Math.ceil(totalUsers / pageSize),
    hasNextPage ? currentPage + 1 : currentPage
  );

  return (
    <div className="p-6 min-h-screen bg-background">
      <UsersHeader />
      <UsersTable
        users={users}
        totalUsers={totalUsers}
        currentPage={currentPage}
        pageSize={pageSize}
        loading={isLoading}
        searchTerm={searchTerm}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSearch={handleSearch}
        onRefresh={() => refetch()}
        totalPages={totalPages}
      />
    </div>
  );
};
